'use client';

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { CollectButton } from '@/components/boardroom/collect-button';
import { HV_TAKEOVER_ADAPTER_ADDRESS } from '@/lib/constants/addresses';
import { getBoardroomLiveGridId } from '@/features/lobby/data/boardroom-grid';
import {
  useBoardroomApiData,
  useBoardroomFeeSourceUgms,
  useCollectableProtocolFees,
} from '@/hooks/use-boardroom-protocol-fees';
import { useWallet } from '@/hooks/use-wallet';

/**
 * HV protocol custom-yield row: TSFM pulls + `pokeAll` on the HV adapter —
 * mirrors `takeover-app-classic/src/app/boardroom/page.tsx` multicall derivation.
 *
 * **Lobby boardroom**: any connected wallet can submit; UX disables when disconnected.
 */
export function BoardroomProtocolCollectFees() {
  const queryClient = useQueryClient();
  const { wallet } = useWallet();
  const hvConfigured = !!(HV_TAKEOVER_ADAPTER_ADDRESS || '').trim();
  const walletConnected = hvConfigured && !!wallet;

  const { data: sourceUgms } = useBoardroomFeeSourceUgms(hvConfigured);

  const { data: boardroomApiData } = useBoardroomApiData(hvConfigured);

  const { data: collectable } = useCollectableProtocolFees(
    sourceUgms,
    boardroomApiData,
    hvConfigured,
  );

  const onSuccess = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['hv-collectable-fees'] });
    void queryClient.invalidateQueries({ queryKey: ['boardroom-api-data'] });
    const live = getBoardroomLiveGridId();
    if (live) {
      void queryClient.invalidateQueries({ queryKey: ['v2-grid', live] });
    }
  }, [queryClient]);

  if (!hvConfigured) {
    return null;
  }

  const pendingYieldUsd = Number(collectable?.totalUsdc ?? 0n) / 1e6;
  const ugms: `0x${string}`[] = collectable?.ugms ?? [];
  const ugmGridIds: bigint[][] = collectable?.ugmGridIds ?? [];
  const tsfms: `0x${string}`[] = (collectable?.newManagers ?? []).map((m) => m.address);
  const tsfmPoolIds: `0x${string}`[][] = (collectable?.newManagers ?? []).map(
    (m) => m.poolIds as `0x${string}`[],
  );
  const oldTsfms: `0x${string}`[] = (collectable?.oldManagers ?? []).map((m) => m.address);

  return (
    <div className="w-full [&_button]:w-full">
      <CollectButton
        pendingYieldUsd={pendingYieldUsd}
        walletConnected={walletConnected}
        ugms={ugms}
        ugmGridIds={ugmGridIds}
        tsfms={tsfms}
        tsfmPoolIds={tsfmPoolIds}
        oldTsfms={oldTsfms}
        onSuccess={onSuccess}
      />
    </div>
  );
}
